// E/G. Privacy governance: DPO, NPC registration, PIAs, the controller/
// processor matrix, data subject rights, retention and suppression.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  CAP, can, fmtDate, fmtDateTime, humanise, legalError, plGet, plPost,
} from "../../lib/platformLegal";
import {
  Button, ClockPill, Empty, ExternalActionNote, Field, Loading, Modal, Note, Section, Stat,
  Table, TonePill, inputClass,
} from "./ui";

const SUBTABS = [
  { key: "governance", label: "DPO & registration" },
  { key: "pia", label: "Impact assessments" },
  { key: "roles", label: "Controller / processor" },
  { key: "requests", label: "Data subject rights" },
  { key: "retention", label: "Retention" },
  { key: "suppression", label: "Suppression" },
];

export default function PrivacySection({ capabilities }) {
  const [tab, setTab] = useState("governance");
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max" role="tablist" data-testid="pl-privacy-tabs">
          {SUBTABS.map((item) => (
            <button key={item.key} role="tab" aria-selected={tab === item.key}
                    onClick={() => setTab(item.key)}
                    data-testid={`pl-privacy-tab-${item.key}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium whitespace-nowrap ${
                      tab === item.key ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 bg-white text-slate-700"}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "governance" && <Governance capabilities={capabilities} />}
      {tab === "pia" && <PiaRegister capabilities={capabilities} />}
      {tab === "roles" && <RoleMatrix capabilities={capabilities} />}
      {tab === "requests" && <Requests capabilities={capabilities} />}
      {tab === "retention" && <Retention capabilities={capabilities} />}
      {tab === "suppression" && <Suppression capabilities={capabilities} />}
    </div>
  );
}

function usePrivacy() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await plGet("/privacy")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

// ---------------------------------------------------------------------------
function Governance({ capabilities }) {
  const [data, reload] = usePrivacy();
  const [editing, setEditing] = useState(null);
  if (!data) return <Loading label="Reading the privacy configuration..." />;
  const { dpo, npcrs } = data.summary;
  const writable = can(capabilities, CAP.dpo);

  return (
    <>
      <Section testId="pl-dpo" title="Data Protection Officer"
               actions={writable && (
                 <Button variant="secondary" onClick={() => setEditing("dpo")}
                         data-testid="pl-dpo-edit">Record</Button>
               )}>
        <div className="flex flex-wrap items-center gap-2">
          <TonePill tone={dpo.status === "appointed"
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-amber-200 bg-amber-50 text-amber-900"} testId="pl-dpo-status">
            {humanise(dpo.status)}
          </TonePill>
          {dpo.name && <span className="text-sm text-slate-800">{dpo.name}</span>}
          {dpo.contact_email && (
            <span className="text-xs text-slate-600">{dpo.contact_email}</span>
          )}
        </div>
        {dpo.status !== "appointed" && (
          <>
            <Note tone="amber" testId="pl-dpo-note">
              No Data Protection Officer has been appointed. No name is ever shown publicly until
              a real person has accepted the role and the appointment record is attached.
            </Note>
            <ExternalActionNote>
              Appointing a DPO is a real-world act; ConfirmPH records that it happened.
            </ExternalActionNote>
          </>
        )}
      </Section>

      <Section testId="pl-npcrs" title="Privacy regulator registration"
               actions={writable && (
                 <Button variant="secondary" onClick={() => setEditing("npcrs")}
                         data-testid="pl-npcrs-edit">Record</Button>
               )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Stat label="Determination" value={humanise(npcrs.determination)} />
          <Stat label="Registration required"
                value={npcrs.registration_required === null || npcrs.registration_required === undefined
                  ? "Undecided"
                  : (npcrs.registration_required ? "Yes" : "No")} />
          <Stat label="Application" value={humanise(npcrs.application_status)} />
        </div>
        <Note tone="slate">
          "Not decided" is recorded separately from "not required", so an undecided position can
          never be mistaken for an assessed one.
        </Note>
        <ExternalActionNote />
      </Section>

      {editing && (
        <GovernanceModal kind={editing} current={editing === "dpo" ? dpo : npcrs}
                         onClose={() => setEditing(null)}
                         onSaved={() => { setEditing(null); reload(); }} />
      )}
    </>
  );
}

function GovernanceModal({ kind, current, onClose, onSaved }) {
  const [form, setForm] = useState(
    kind === "dpo"
      ? { status: current.status || "not_appointed", name: current.name || "",
          contact_email: current.contact_email || "", appointed_at: current.appointed_at || "",
          reason: "" }
      : { determination: current.determination || "not_determined",
          determination_basis: current.determination_basis || "",
          application_status: current.application_status || "not_started",
          registration_id: current.registration_id || "", reason: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await plPost(`/privacy/${kind === "dpo" ? "dpo" : "npcrs"}`, form);
      toast.success("Recorded.");
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={kind === "dpo" ? "Data Protection Officer" : "Privacy registration"}
           onClose={onClose} testId={`pl-${kind}-modal`}>
      {kind === "dpo" ? (
        <>
          <Field label="Status">
            <select className={inputClass} value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {["not_appointed", "appointment_in_progress", "appointed", "vacant",
                "not_applicable_review_required"].map((s) => (
                <option key={s} value={s}>{humanise(s)}</option>
              ))}
            </select>
          </Field>
          <Field label="Name"><input className={inputClass} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Contact email"><input className={inputClass} value={form.contact_email}
                 onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
          <Field label="Appointment date"><input type="date" className={inputClass}
                 value={form.appointed_at}
                 onChange={(e) => setForm({ ...form, appointed_at: e.target.value })} /></Field>
          <Note tone="amber">
            Recording a DPO as appointed needs a name, a contact and the appointment record.
            Attach the document from the Evidence tab first, then set the status here.
          </Note>
        </>
      ) : (
        <>
          <Field label="Determination">
            <select className={inputClass} value={form.determination}
                    onChange={(e) => setForm({ ...form, determination: e.target.value })}>
              {["not_determined", "registration_required", "exemption_considered",
                "not_required", "legal_review_required"].map((s) => (
                <option key={s} value={s}>{humanise(s)}</option>
              ))}
            </select>
          </Field>
          <Field label="What was the determination based on?">
            <textarea className={inputClass} rows={3} value={form.determination_basis}
                      onChange={(e) => setForm({ ...form, determination_basis: e.target.value })} />
          </Field>
          <Field label="Application status">
            <select className={inputClass} value={form.application_status}
                    onChange={(e) => setForm({ ...form, application_status: e.target.value })}>
              {["not_started", "preparing", "submitted", "registered", "expired", "rejected",
                "not_applicable"].map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </select>
          </Field>
          <Field label="Registration reference"><input className={inputClass}
                 value={form.registration_id}
                 onChange={(e) => setForm({ ...form, registration_id: e.target.value })} /></Field>
        </>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} busy={busy} data-testid={`pl-${kind}-save`}>Save</Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function PiaRegister() {
  const [data] = usePrivacy();
  if (!data) return <Loading />;
  return (
    <Section testId="pl-pia" title="Privacy impact assessments"
             description="Every system that processes personal data is registered. Listing a
                          system is not assessing it — an unassessed one says so.">
      <Table head={["System", "Status", "Legal basis", "Owner", "Next review"]}
             testId="pl-pia-table" minWidth={620}>
        {data.pia.map((row) => (
          <tr key={row.key} data-testid={`pl-pia-${row.key}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{row.system}</div>
              {row.purpose && <div className="text-xs text-slate-500">{row.purpose}</div>}
            </td>
            <td className="py-2 pr-3">
              <TonePill tone={row.status === "approved"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-900"}>
                {humanise(row.status)}
              </TonePill>
            </td>
            <td className="py-2 pr-3 text-xs">{humanise(row.legal_basis_review)}</td>
            <td className="py-2 pr-3 text-xs">{row.owner || "—"}</td>
            <td className="py-2 pr-3 text-xs">{fmtDate(row.next_review_at)}</td>
          </tr>
        ))}
      </Table>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function RoleMatrix() {
  const [data] = usePrivacy();
  if (!data) return <Loading />;
  return (
    <Section testId="pl-data-roles" title="Controller / processor matrix"
             description="ConfirmPH's role is recorded per dataset. Several are genuinely mixed,
                          and there is no global 'we are only a processor' claim anywhere.">
      <Table head={["Dataset", "Whose data", "ConfirmPH", "Business", "Legal basis"]}
             testId="pl-roles-table" minWidth={680}>
        {data.data_roles.map((row) => (
          <tr key={row.key} data-testid={`pl-role-${row.key}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{row.dataset}</div>
              <div className="text-xs text-slate-500">{row.purpose}</div>
            </td>
            <td className="py-2 pr-3 text-xs">{row.whose_data}</td>
            <td className="py-2 pr-3 text-xs">
              {humanise(row.confirmph_role)}
              {row.confirmph_role === "not_determined" && row.suggested_confirmph_role && (
                <div className="text-[11px] text-slate-400">
                  suggested: {humanise(row.suggested_confirmph_role)}
                </div>
              )}
            </td>
            <td className="py-2 pr-3 text-xs">{humanise(row.tenant_role)}</td>
            <td className="py-2 pr-3 text-xs">{humanise(row.legal_basis_review)}</td>
          </tr>
        ))}
      </Table>
      <Note tone="slate">{data.summary.note}</Note>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Requests({ capabilities }) {
  const [data, setData] = useState(null);
  const writable = can(capabilities, CAP.privacyRequests);

  const load = useCallback(async () => {
    try { setData(await plGet("/privacy/requests")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading privacy requests..." />;

  const board = data.dashboard;
  return (
    <>
      <Section testId="pl-dsr-board" title="Data subject rights" description={board.note}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Stat label="Open" value={board.open} testId="pl-dsr-open" />
          <Stat label="Overdue" value={board.overdue}
                tone={board.overdue ? "text-rose-700" : "text-slate-900"} />
          <Stat label="Awaiting identity" value={board.awaiting_identity} />
          <Stat label="Retention conflicts" value={board.retention_conflicts} />
          <Stat label="Response days" value={board.response_days} />
        </div>
      </Section>
      <Section testId="pl-dsr">
        {data.requests.length === 0 ? (
          <Empty testId="pl-dsr-empty">No open requests.</Empty>
        ) : (
          <Table head={["Reference", "Right", "Identity", "Due", "Status"]}
                 testId="pl-dsr-table" minWidth={620}>
            {data.requests.map((row) => (
              <tr key={row.id} data-testid={`pl-dsr-${row.reference}`}>
                <td className="py-2 pr-3 font-mono text-xs">{row.reference}
                  <div className="text-[11px] text-slate-500">{fmtDateTime(row.received_at)}</div>
                </td>
                <td className="py-2 pr-3 text-xs">{row.label}</td>
                <td className="py-2 pr-3 text-xs">{humanise(row.identity_state)}</td>
                <td className="py-2 pr-3"><ClockPill clock={row.clock} /></td>
                <td className="py-2 pr-3 text-xs">{humanise(row.status)}</td>
              </tr>
            ))}
          </Table>
        )}
        {writable && (
          <Note tone="slate">
            Nothing is disclosed or erased before the requester's identity is verified, and a
            deletion request is resolved per data area with the basis recorded for anything kept.
          </Note>
        )}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
function Retention({ capabilities }) {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try { setData(await plGet("/retention")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the retention schedule..." />;

  const dryRun = async (key) => {
    try { setPreview(await plGet(`/retention/${key}/dry-run`)); }
    catch (err) { toast.error(legalError(err)); }
  };

  return (
    <Section testId="pl-retention" title="Data retention schedule"
             description={data.summary.note}>
      <Table head={["Category", "Status", "Active", "Archive", "Action", ""]}
             testId="pl-retention-table" minWidth={680}>
        {data.policies.map((row) => (
          <tr key={row.key} data-testid={`pl-retention-${row.key}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{row.label}</div>
              <div className="text-xs text-slate-500">{row.purpose}</div>
            </td>
            <td className="py-2 pr-3">
              <TonePill tone={row.status === "approved"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-900"}>
                {humanise(row.status)}
              </TonePill>
            </td>
            <td className="py-2 pr-3 text-xs">{row.active_period_days ?? "—"}</td>
            <td className="py-2 pr-3 text-xs">{row.archive_period_days ?? "—"}</td>
            <td className="py-2 pr-3 text-xs">{humanise(row.deletion_action)}</td>
            <td className="py-2 pr-3">
              <Button variant="secondary" onClick={() => dryRun(row.key)}
                      data-testid={`pl-dryrun-${row.key}`}>Preview</Button>
            </td>
          </tr>
        ))}
      </Table>
      <Note tone="amber" testId="pl-retention-note">
        There is no automated retention deletion job in ConfirmPH. Preview counts what a policy
        would affect; it reads nothing in full, changes nothing and deletes nothing.
      </Note>

      {preview && (
        <Modal title={`Preview: ${humanise(preview.key)}`} onClose={() => setPreview(null)}
               testId="pl-dryrun-modal">
          {preview.configured === false ? (
            <Empty>{preview.note}</Empty>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-slate-500">Collection</dt>
              <dd className="text-right">{preview.collection}</dd>
              <dt className="text-slate-500">Cutoff</dt>
              <dd className="text-right">{fmtDate(preview.cutoff_date)}</dd>
              <dt className="text-slate-500">Records total</dt>
              <dd className="text-right tabular-nums">{preview.records_total}</dd>
              <dt className="text-slate-500">Past retention</dt>
              <dd className="text-right tabular-nums">{preview.records_past_retention}</dd>
            </dl>
          )}
          <Note tone="amber">{preview.note}</Note>
          {preview.legal_hold_rule && <Note tone="slate">{preview.legal_hold_rule}</Note>}
        </Modal>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Suppression() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await plGet("/suppressions")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;

  return (
    <Section testId="pl-suppression" title="Marketing suppression"
             description="The list every outreach path consults. Once someone is on it, ordinary
                          marketing tools cannot contact them again.">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Active" value={data.summary.active} testId="pl-suppression-active" />
        <Stat label="Lifted" value={data.summary.lifted} />
        {data.summary.by_channel.map((row) => (
          <Stat key={row.channel} label={humanise(row.channel)} value={row.count} />
        ))}
      </div>
      <Note tone="slate" testId="pl-transactional-note">
        {data.summary.transactional_note}
      </Note>
      {data.suppressions.length > 0 && (
        <Table head={["Channel", "Contact", "Reason", "Since"]} testId="pl-suppression-table">
          {data.suppressions.slice(0, 50).map((row) => (
            <tr key={row.id}>
              <td className="py-2 pr-3 text-xs">{humanise(row.channel)}</td>
              <td className="py-2 pr-3 text-xs break-all">{row.identifier || row.user_id}</td>
              <td className="py-2 pr-3 text-xs">{humanise(row.reason)}</td>
              <td className="py-2 pr-3 text-xs">{fmtDate(row.suppressed_at)}</td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}
